import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Droplets,
  Recycle,
  Gauge,
  CloudRain,
  Waves,
  Sprout,
  Activity,
  Wrench,
  Package,
} from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.2, 0.7, 0.2, 1] } },
};
const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.04 } } };

/**
 * Engineering services — plain industry language, no marketing fluff.
 * Card style locked to HowWeWork register: white card + navy hairline + soft shadow,
 * teal-tinted icon chip with navy glyph, teal mono eyebrow, navy sentence-case title.
 */
const U = (id) => `https://images.unsplash.com/${id}?w=1200&q=80&auto=format&fit=crop`;

const SERVICES = [
  {
    slug: "purification",
    icon: Droplets,
    img: "/library/purification/purification_11.webp",
    name: "Water Purification",
    line: "Water purification for all source waters — boreholes, surface, municipal — tailored to SANS 241 and WHO guidelines.",
  },
  {
    slug: "wastewater",
    icon: Recycle,
    img: "/generated/wastewater-package-plant.webp",
    name: "Wastewater Treatment",
    line: "Packaged plants and tertiary polishing tailored to your effluent profile — reuse-ready where it makes sense.",
  },
  {
    slug: "pumps",
    icon: Gauge,
    img: "/mockups/eq_pumps_borehole.webp",
    name: "Pumps & Pressure",
    line: "Pumping systems tailored to the duty — from VSD booster sets and submersibles to dewatering, fire and transfer.",
  },
  {
    slug: "harvesting",
    icon: CloudRain,
    img: "/mockups/eq_harvesting_tank_exterior.webp",
    name: "Source & Storage",
    line: "Source water made reliable — boreholes, surface intake, rainwater capture and blended reservoir storage.",
  },
  {
    slug: "pool-spa",
    icon: Waves,
    img: "/mockups/eq_pool_afm.webp",
    name: "Pool & Spa",
    line: "Pool and spa systems tailored to the venue — AFM filtration, salt chlorination, UV-C polishing and VSD circulation.",
  },
  {
    slug: "irrigation",
    icon: Sprout,
    img: "/mockups/eq_irrigation_drip.webp",
    name: "Irrigation",
    line: "Irrigation systems tailored to landscape and crop — drip, sub-surface, micro-jet, smart control. Includes autonomous solar-array cleaning.",
  },
  {
    slug: "monitoring",
    icon: Activity,
    img: "/mockups/eq_monitoring_dashboard.webp",
    name: "Online Monitoring & Control",
    line: "Live oversight of every critical asset — sensor telemetry, leak detection, alarms and audit-grade logging.",
  },
  {
    slug: "service",
    icon: Wrench,
    img: "/projects/likweti/likweti_03.webp",
    name: "Service & SLA",
    line: "SLA and maintenance plans for HidroVerse installs and existing systems that need expert oversight.",
  },
  {
    slug: "rentals",
    icon: Package,
    img: U("photo-1450101499163-c8848c66ca85"),
    name: "Rentals & As-a-Service",
    line: "Bridge equipment, lease-to-own and water-as-a-service options for sites that prefer opex over capex.",
  },
];

export default function HomeServices() {
  return (
    <section
      id="services"
      className="px-6 md:px-12 lg:px-20 py-14 md:py-20"
      style={{ background: "#FAFAF7" }}
      data-testid="home-services"
    >
      <div className="max-w-7xl mx-auto">
        {/* Section head — matches HowWeWork register */}
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-16 items-end mb-10 md:mb-14">
          <div className="lg:col-span-7">
            <div
              className="font-mono uppercase mb-5"
              style={{ fontSize: "0.62rem", letterSpacing: "0.26em", color: "#1FB6C1" }}
            >
              Services
            </div>
            <h2
              className="font-display"
              style={{
                fontSize: "clamp(2rem, 4.5vw, 3.4rem)",
                lineHeight: 1.06,
                letterSpacing: "-0.02em",
                fontWeight: 700,
                color: "#0C2A40",
              }}
            >
              Multiple water services.<br />
              <span style={{ color: "#1FB6C1" }}>One accountable supplier.</span>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p
              className="font-light leading-relaxed"
              style={{ fontSize: "1rem", color: "#1A1F25" }}
            >
              Every water discipline a commercial site needs — designed, installed, monitored
              and serviced in-house. Pick the system that matches the brief; we'll show you exactly
              how we deliver it.
            </p>
          </div>
        </div>

        {/* Cards — same elevation/border as HowWeWork stage cards */}
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={stagger}
          className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6"
          data-testid="home-services-grid"
        >
          {SERVICES.map((s) => {
            const Icon = s.icon;
            return (
              <motion.div key={s.slug} variants={fadeUp}>
                <Link
                  to={`/services/${s.slug}`}
                  className="group relative flex flex-col h-full overflow-hidden transition-all duration-300"
                  style={{
                    background: "#FFFFFF",
                    border: "1px solid rgba(12,42,64,0.08)",
                    borderRadius: 10,
                    boxShadow:
                      "0 1px 2px rgba(12,42,64,0.04), 0 12px 32px -16px rgba(12,42,64,0.10)",
                  }}
                  data-testid={`home-service-${s.slug}`}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "rgba(31,182,193,0.45)";
                    e.currentTarget.style.boxShadow =
                      "0 1px 2px rgba(12,42,64,0.04), 0 24px 48px -16px rgba(12,42,64,0.18)";
                    e.currentTarget.style.transform = "translateY(-3px)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "rgba(12,42,64,0.08)";
                    e.currentTarget.style.boxShadow =
                      "0 1px 2px rgba(12,42,64,0.04), 0 12px 32px -16px rgba(12,42,64,0.10)";
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                >
                  {/* Pristine white text block on top */}
                  <div className="p-7 md:p-8 flex-1">
                    <span
                      className="inline-flex items-center justify-center mb-6"
                      style={{
                        width: 44,
                        height: 44,
                        borderRadius: 8,
                        background: "rgba(31,182,193,0.10)",
                        color: "#0C2A40",
                      }}
                    >
                      <Icon size={22} strokeWidth={1.75} />
                    </span>

                    <h3
                      className="font-display mb-3"
                      style={{
                        fontSize: "1.25rem",
                        lineHeight: 1.25,
                        letterSpacing: "-0.01em",
                        color: "#0C2A40",
                        fontWeight: 600,
                      }}
                    >
                      {s.name}
                    </h3>

                    <p
                      className="font-light leading-relaxed mb-6"
                      style={{ fontSize: "0.92rem", color: "#1A1F25" }}
                    >
                      {s.line}
                    </p>

                    <div
                      className="flex items-center justify-between pt-4"
                      style={{ borderTop: "1px solid rgba(12,42,64,0.08)" }}
                    >
                      <span
                        className="font-mono uppercase"
                        style={{
                          fontSize: "0.6rem",
                          letterSpacing: "0.24em",
                          color: "#0C2A40",
                          fontWeight: 700,
                        }}
                      >
                        Read more
                      </span>
                      <span
                        aria-hidden
                        className="transition-transform duration-300 group-hover:translate-x-1"
                        style={{ color: "#1FB6C1", fontWeight: 700 }}
                      >
                        →
                      </span>
                    </div>
                  </div>

                  {/* Real photo strip at the bottom — full bleed, no text overlap */}
                  <div
                    className="relative overflow-hidden"
                    style={{ height: 140 }}
                    aria-hidden
                  >
                    <img
                      src={s.img}
                      alt=""
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                    />
                    {/* Subtle navy wash to keep image in palette */}
                    <div
                      className="absolute inset-0 pointer-events-none"
                      style={{
                        background:
                          "linear-gradient(to top, rgba(12,42,64,0.18) 0%, rgba(12,42,64,0) 60%)",
                      }}
                    />
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Footer line — see all */}
        <div className="mt-10 md:mt-12 flex flex-wrap items-center justify-between gap-4">
          <p
            className="font-light"
            style={{ fontSize: "0.92rem", color: "rgba(12,42,64,0.65)" }}
          >
            Not sure which service applies? Tell us the site and we'll point to the right stack.
          </p>
          <Link
            to="/services"
            className="inline-flex items-center gap-2 font-mono uppercase"
            style={{ fontSize: "0.66rem", letterSpacing: "0.24em", color: "#0C2A40", fontWeight: 700 }}
            data-testid="home-services-see-all"
          >
            See all services <span aria-hidden style={{ color: "#1FB6C1" }}>→</span>
          </Link>
        </div>
      </div>
    </section>
  );
}
